<div class="role2-020000-scope-of-filing-container">
  <p-toast></p-toast>
  <app-sheet-loader [visible]="isLoading" />

  <!--
    The gate lives on the CARD, not on the <form> inside it. Gating only the
    form left the card title, both section strips and <submit-for-validation>
    painted underneath the spinner — all four sit inside the card but outside
    the form. <app-sheet-loader> stays a SIBLING above the card so it is not
    removed by this same gate; see scripts/check-sheet-loaders.mjs.
  -->
  <p-card
    [header]="formTitle"
    [subheader]="isDev ? '[020000] Scope of filing' : ''"
    *ngIf="!isLoading && schema && scopeForm"
  >
    <div class="bg-gray-200 text-black-700 text-sm font-medium px-2 py-1">
      Disclosure on scope of filing
    </div>
    <div class="bg-gray-200 text-black-700 text-sm font-medium px-3 py-1">
      Particulars of Financial Statements and Reports
    </div>

    <!-- Form Content -->
    <form
      [formGroup]="scopeForm"
      class="scope-form"
      appFieldLinkTarget
      fieldLinkSheet="scope-of-filing"
      [fieldLinkControls]="schema.controls"
    >
      <!-- Section 1: Particulars of Financial Statements and Reports -->
      <div class="form-section">
        <div class="form-grid">
          <!-- Render ALL fields in schema order -->
          <ng-container *ngFor="let control of schema.controls">
            <!-- Only show fields that pass all checks: shouldShowField() + !hideWhen + showInSection -->
            <div
              class="form-field"
              [attr.data-qname]="control.qname"
              *ngIf="
                shouldShowField(control) &&
                !shouldHideField(control.qname) &&
                shouldShowInSection(control.qname, 'particulars')
              "
            >
              <label [for]="control.qname" class="field-label">
                {{ control.label }}<app-required-marker [when]="isFieldRequired(control)" />
              </label>

              <!-- Text Input -->
              <div
                class="field-input-wrapper"
                *ngIf="control.ui.control === 'text'"
              >
                <input
                  pInputText
                  [id]="control.qname"
                  [formControlName]="control.qname"
                  [placeholder]="control | placeholder"
                  [readonly]="control.ui.readonly || false"
                  class="w-full"
                />

                <small
                  class="error-message"
                  *ngIf="
                    getControl(control.qname)?.invalid &&
                    getControl(control.qname)?.touched
                  "
                >
                  <span *ngIf="getControl(control.qname)?.errors?.['required']">
                    {{
                      control.error?.required ||
                        control.label + " is required." ||
                        "This is a mandatory field."
                    }}
                  </span>
                  <span *ngIf="getControl(control.qname)?.errors?.['pattern']">
                    {{
                      control.error?.pattern ||
                        "Invalid format. " + (control.ui.placeholder || "")
                    }}
                  </span>
                </small>
              </div>

              <!-- Date Picker -->
              <div
                class="field-input-wrapper"
                *ngIf="control.ui.control === 'date'"
              >
                <p-datepicker
                  [inputId]="control.qname"
                  [formControlName]="control.qname"
                  dateFormat="yy-mm-dd"
                  [showIcon]="true"
                  iconDisplay="input"
                  [placeholder]="control | fixedPlaceholder: 'Select a date'"
                  class="w-full"
                  [class.p-invalid]="
                    getControl(control.qname)?.invalid &&
                    getControl(control.qname)?.touched
                  "
                  (onInput)="onInputChange($event)"
                ></p-datepicker>

                <small
                  class="error-message"
                  *ngIf="
                    getControl(control.qname)?.invalid &&
                    getControl(control.qname)?.touched
                  "
                >
                  <span *ngIf="getControl(control.qname)?.errors?.['required']">
                    {{
                      control.error?.required ||
                        control.label + " is required." ||
                        "This is a mandatory field."
                    }}
                  </span>
                </small>

                <!-- Combined warning messages (non-blocking) -->
                <app-warning-message
                  [warnings]="getAllWarnings(control.qname)"
                ></app-warning-message>
              </div>

              <!-- Select/Dropdown (with enumSource) -->
              <div
                class="field-input-wrapper"
                *ngIf="control.ui.control === 'select' && control.ui.enumSource"
              >
                <p-select
                  [inputId]="control.qname"
                  [formControlName]="control.qname"
                  [options]="getDropdownOptions(control.ui.enumSource)"
                  optionLabel="label"
                  optionValue="value"
                  [placeholder]="control | fixedPlaceholder: 'Please select'"
                  class="w-full"
                  [filter]="true"
                  filterPlaceholder="Search..."
                  [showClear]="true"
                ></p-select>

                <small
                  class="error-message"
                  *ngIf="
                    getControl(control.qname)?.invalid &&
                    getControl(control.qname)?.touched
                  "
                >
                  <span *ngIf="getControl(control.qname)?.errors?.['required']">
                    {{
                      control.error?.required ||
                        control.label + " is required." ||
                        "This is a mandatory field."
                    }}
                  </span>
                </small>
              </div>

              <!-- Select/Dropdown (with static options) -->
              <div
                class="field-input-wrapper"
                *ngIf="control.ui.control === 'select' && control.ui.static"
              >
                <p-select
                  [inputId]="control.qname"
                  [formControlName]="control.qname"
                  [options]="getStaticOptions(control.ui.static)"
                  optionLabel="label"
                  optionValue="value"
                  [placeholder]="control | fixedPlaceholder: 'Please select'"
                  class="w-full"
                  [filter]="true"
                  filterPlaceholder="Search..."
                  [showClear]="true"
                ></p-select>

                <small
                  class="error-message"
                  *ngIf="
                    getControl(control.qname)?.invalid &&
                    getControl(control.qname)?.touched
                  "
                >
                  <span *ngIf="getControl(control.qname)?.errors?.['required']">
                    {{
                      control.error?.required ||
                        control.label + " is required." ||
                        "This is a mandatory field."
                    }}
                  </span>
                </small>
              </div>
            </div>
          </ng-container>
        </div>
      </div>

      <!-- Principal Activities Table -->
      <div class="form-section mt-6">
        <h3 class="section-header">Principal Activities (MSIC 2008)</h3>

        <table class="business-table">
          <thead>
            <tr>
              <th style="width: 20%">No.</th>
              <th style="width: 25%">MSIC Code</th>
              <th style="width: 55%">Description of Business</th>
            </tr>
          </thead>
          <tbody>
            <!-- Business 1 (Required) -->
            <tr>
              <td class="business-label">
                Business 1<app-required-marker [when]="true" />
              </td>
              <td>
                <p-select
                  formControlName="ssmt:MSICCode[ssmt:BusinessOneMember]"
                  [options]="msicOptions"
                  optionLabel="displayLabel"
                  optionValue="code"
                  placeholder="Please select"
                  [filter]="true"
                  filterPlaceholder="Search by code or description..."
                  scrollHeight="350px"
                  [virtualScroll]="true"
                  [virtualScrollItemSize]="50"
                  (onChange)="onMsicCodeChange($event, 1)"
                  class="w-full msic-dropdown"
                  [class.p-invalid]="
                    getControl('ssmt:MSICCode[ssmt:BusinessOneMember]')
                      ?.invalid &&
                    getControl('ssmt:MSICCode[ssmt:BusinessOneMember]')?.touched
                  "
                  [showClear]="true"
                >
                  <ng-template #selectedItem let-selectedOption>
                    <span class="font-medium">{{ selectedOption?.code }}</span>
                  </ng-template>
                  <ng-template #item let-option>
                    <div class="msic-option">
                      <span class="msic-code">{{ option.code }}</span>
                      <span class="msic-desc">{{ option.descriptionEn }}</span>
                    </div>
                  </ng-template>
                </p-select>
                <small
                  class="error-message"
                  *ngIf="
                    getControl('ssmt:MSICCode[ssmt:BusinessOneMember]')
                      ?.invalid &&
                    getControl('ssmt:MSICCode[ssmt:BusinessOneMember]')?.touched
                  "
                >
                  <span
                    *ngIf="
                      getControl('ssmt:MSICCode[ssmt:BusinessOneMember]')
                        ?.errors?.['required']
                    "
                    >This is a mandatory field.</span
                  >
                </small>
              </td>
              <td>
                <!--
                  Derived + disabled (schema `ui.readonly`, spec 02 #10
                  `auto_managed: from_field:[#f9]`): the value is written by
                  populateDescriptionsFromMsicCodes() and the user can never
                  type here. Angular runs no validators on a disabled control,
                  so this cell renders no mandatory error — the MSIC Code
                  driver beside it carries it. Do not re-add a p-invalid /
                  error-message binding; it could never fire.
                -->
                <input
                  pInputText
                  formControlName="ssmt:DescriptionOfBusiness[ssmt:BusinessOneMember]"
                  placeholder="e.g., Wholesale of general merchandise"
                  class="w-full"
                  readonly
                />
              </td>
            </tr>

            <!-- Business 2 (Optional) -->
            <tr>
              <td class="business-label">
                Business 2 <br />
                <span class="text-gray-500">(Optional)</span>
              </td>
              <td>
                <p-select
                  formControlName="ssmt:MSICCode[ssmt:BusinessTwoMember]"
                  [options]="msicOptions"
                  optionLabel="displayLabel"
                  optionValue="code"
                  placeholder="Please select"
                  [filter]="true"
                  filterPlaceholder="Search by code or description..."
                  scrollHeight="350px"
                  [virtualScroll]="true"
                  [virtualScrollItemSize]="50"
                  (onChange)="onMsicCodeChange($event, 2)"
                  class="w-full msic-dropdown"
                  [showClear]="true"
                >
                  <ng-template #selectedItem let-selectedOption>
                    <span class="font-medium">{{ selectedOption?.code }}</span>
                  </ng-template>
                  <ng-template #item let-option>
                    <div class="msic-option">
                      <span class="msic-code">{{ option.code }}</span>
                      <span class="msic-desc">{{ option.descriptionEn }}</span>
                    </div>
                  </ng-template>
                </p-select>
              </td>
              <td>
                <input
                  pInputText
                  formControlName="ssmt:DescriptionOfBusiness[ssmt:BusinessTwoMember]"
                  class="w-full"
                  readonly
                />
              </td>
            </tr>

            <!-- Business 3 (Optional) -->
            <tr>
              <td class="business-label">
                Business 3 <br />
                <span class="text-gray-500">(Optional)</span>
              </td>
              <td>
                <p-select
                  formControlName="ssmt:MSICCode[ssmt:BusinessThreeMember]"
                  [options]="msicOptions"
                  optionLabel="displayLabel"
                  optionValue="code"
                  placeholder="Please select"
                  [filter]="true"
                  filterPlaceholder="Search by code or description..."
                  scrollHeight="350px"
                  [virtualScroll]="true"
                  [virtualScrollItemSize]="50"
                  (onChange)="onMsicCodeChange($event, 3)"
                  class="w-full msic-dropdown"
                  [showClear]="true"
                >
                  <ng-template #selectedItem let-selectedOption>
                    <span class="font-medium">{{ selectedOption?.code }}</span>
                  </ng-template>
                  <ng-template #item let-option>
                    <div class="msic-option">
                      <span class="msic-code">{{ option.code }}</span>
                      <span class="msic-desc">{{ option.descriptionEn }}</span>
                    </div>
                  </ng-template>
                </p-select>
              </td>
              <td>
                <input
                  pInputText
                  formControlName="ssmt:DescriptionOfBusiness[ssmt:BusinessThreeMember]"
                  class="w-full"
                  readonly
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Companies Limited By Guarantee (CLBG) Table - Only for FS-CLBG and KFI-CLBG -->
      <div class="form-section mt-6" *ngIf="shouldShowCLBGTable()">
        <h3 class="section-header">
          Disclosure of Companies Limited By Guarantee
        </h3>

        <table class="business-table">
          <thead>
            <tr>
              <th style="width: 20%">No.</th>
              <th style="width: 40%">
                Categories of Objects<app-required-marker [when]="true" />
              </th>
              <th style="width: 40%">
                Sub-categories of Objects<app-required-marker [when]="true" />
              </th>
            </tr>
          </thead>
          <tbody>
            <!-- Object 1 (Required) -->
            <tr>
              <td class="business-label">Object 1</td>
              <td>
                <p-select
                  formControlName="ssmt-clbg:CategoriesOfObjectsObject1"
                  [options]="
                    getDropdownOptions(
                      'TypeOfDisclosureOnCategoriesOfObjectsOfCompaniesLimitedByGuarantee'
                    )
                  "
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Please select"
                  [filter]="true"
                  filterPlaceholder="Search..."
                  class="w-full"
                  [class.p-invalid]="
                    getControl('ssmt-clbg:CategoriesOfObjectsObject1')
                      ?.invalid &&
                    getControl('ssmt-clbg:CategoriesOfObjectsObject1')?.touched
                  "
                  [showClear]="true"
                ></p-select>
                <small
                  class="error-message"
                  *ngIf="
                    getControl('ssmt-clbg:CategoriesOfObjectsObject1')
                      ?.invalid &&
                    getControl('ssmt-clbg:CategoriesOfObjectsObject1')?.touched
                  "
                >
                  <span
                    *ngIf="
                      getControl('ssmt-clbg:CategoriesOfObjectsObject1')
                        ?.errors?.['required']
                    "
                    >This is a mandatory field.</span
                  >
                </small>
              </td>
              <td>
                <p-select
                  formControlName="ssmt-clbg:SubCategoriesOfObjectsObject1"
                  [options]="
                    getDropdownOptions(
                      'TypeOfDisclosureOnSubCategoriesOfObjectsOfCompaniesLimitedByGuarantee'
                    )
                  "
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Please select"
                  [filter]="true"
                  filterPlaceholder="Search..."
                  class="w-full"
                  [class.p-invalid]="
                    getControl('ssmt-clbg:SubCategoriesOfObjectsObject1')
                      ?.invalid &&
                    getControl('ssmt-clbg:SubCategoriesOfObjectsObject1')
                      ?.touched
                  "
                  [showClear]="true"
                ></p-select>
                <small
                  class="error-message"
                  *ngIf="
                    getControl('ssmt-clbg:SubCategoriesOfObjectsObject1')
                      ?.invalid &&
                    getControl('ssmt-clbg:SubCategoriesOfObjectsObject1')
                      ?.touched
                  "
                >
                  This is a mandatory field.
                </small>
              </td>
            </tr>

            <!-- Object 2 (Optional) -->
            <tr>
              <td class="business-label">Object 2</td>
              <td>
                <p-select
                  formControlName="ssmt-clbg:CategoriesOfObjectsObject2"
                  [options]="
                    getDropdownOptions(
                      'TypeOfDisclosureOnCategoriesOfObjectsOfCompaniesLimitedByGuarantee'
                    )
                  "
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Please select"
                  [filter]="true"
                  filterPlaceholder="Search..."
                  class="w-full"
                  [showClear]="true"
                ></p-select>
              </td>
              <td>
                <p-select
                  formControlName="ssmt-clbg:SubCategoriesOfObjectsObject2"
                  [options]="
                    getDropdownOptions(
                      'TypeOfDisclosureOnSubCategoriesOfObjectsOfCompaniesLimitedByGuarantee'
                    )
                  "
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Please select"
                  [filter]="true"
                  filterPlaceholder="Search..."
                  class="w-full"
                  [showClear]="true"
                ></p-select>
              </td>
            </tr>

            <!-- Object 3 (Optional) -->
            <tr>
              <td class="business-label">Object 3</td>
              <td>
                <p-select
                  formControlName="ssmt-clbg:CategoriesOfObjectsObject3"
                  [options]="
                    getDropdownOptions(
                      'TypeOfDisclosureOnCategoriesOfObjectsOfCompaniesLimitedByGuarantee'
                    )
                  "
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Please select"
                  [filter]="true"
                  filterPlaceholder="Search..."
                  class="w-full"
                  [showClear]="true"
                ></p-select>
              </td>
              <td>
                <p-select
                  formControlName="ssmt-clbg:SubCategoriesOfObjectsObject3"
                  [options]="
                    getDropdownOptions(
                      'TypeOfDisclosureOnSubCategoriesOfObjectsOfCompaniesLimitedByGuarantee'
                    )
                  "
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Please select"
                  [filter]="true"
                  filterPlaceholder="Search..."
                  class="w-full"
                  [showClear]="true"
                ></p-select>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="bg-gray-200 text-black-700 text-sm font-medium px-3 py-1">
        Approved Application From The Registrar Or Minister
      </div>

      <div class="form-section">
        <div class="form-grid">
          <!-- Render all fields in schema order (parent field + conditional fields + method fields) -->
          <ng-container *ngFor="let control of schema.controls">
            <!-- Text Input Fields (Inherited - Parent Field & Method Fields) -->
            <!-- Only show fields that belong to this section and pass hiding checks -->
            <!-- Exclude business table fields (they have their own dedicated table) -->
            <div
              class="form-field"
              [attr.data-qname]="control.qname"
              *ngIf="
                control.ui.control === 'text' &&
                !control.ui.businessTable &&
                !shouldHideField(control.qname) &&
                shouldShowInSection(control.qname, 'approved-application')
              "
            >
              <label [for]="control.qname" class="field-label">
                {{ control.label }}<app-required-marker [when]="isFieldRequired(control)" />
              </label>

              <div class="field-input-wrapper">
                <input
                  pInputText
                  [id]="control.qname"
                  [formControlName]="control.qname"
                  [readonly]="control.ui.readonly || false"
                  class="w-full"
                />
              </div>
            </div>

            <!-- Conditional Select/Dropdown Fields (only shown when parent field = "Yes" and in this section) -->
            <!-- Exclude business table fields (they have their own dedicated table) -->
            <div
              class="form-field"
              [attr.data-qname]="control.qname"
              *ngIf="
                control.ui.control === 'select' &&
                !control.ui.businessTable &&
                control.ui.conditionalOn &&
                getControl(control.ui.conditionalOn)?.value ===
                  control.ui.showWhen &&
                shouldShowInSection(control.qname, 'approved-application')
              "
            >
              <label [for]="control.qname" class="field-label">
                {{ control.label }}<app-required-marker [when]="isFieldRequired(control)" />
              </label>

              <div class="field-input-wrapper">
                <p-select
                  [inputId]="control.qname"
                  [formControlName]="control.qname"
                  [options]="getDropdownOptions(control.ui.enumSource)"
                  optionLabel="label"
                  optionValue="value"
                  [placeholder]="control | fixedPlaceholder: 'Please select'"
                  class="w-full"
                  [filter]="true"
                  filterPlaceholder="Search..."
                  [showClear]="true"
                ></p-select>

                <small
                  class="error-message"
                  *ngIf="
                    getControl(control.qname)?.invalid &&
                    getControl(control.qname)?.touched
                  "
                >
                  <span *ngIf="getControl(control.qname)?.errors?.['required']">
                    {{
                      control.error?.required ||
                        control.label + " is required." ||
                        "This is a mandatory field."
                    }}
                  </span>
                </small>
              </div>
            </div>
          </ng-container>
        </div>
      </div>

      <div class="bg-gray-200 text-black-700 text-sm font-medium px-3 py-1">
        Changes From Subsequent XBRL Financial Statements Filing
      </div>

      <div class="form-section">
        <div class="form-grid">
          <!-- Text Input Fields (Inherited - readonly) -->
          <!-- Exclude business table fields (they have their own dedicated table) -->
          <ng-container *ngFor="let control of schema.controls">
            <div
              class="form-field"
              [attr.data-qname]="control.qname"
              *ngIf="
                control.ui.control === 'text' &&
                !control.ui.businessTable &&
                shouldShowInSection(control.qname, 'subsequent-changes')
              "
            >
              <label [for]="control.qname" class="field-label">
                {{ control.label }}<app-required-marker [when]="isFieldRequired(control)" />
              </label>

              <div class="field-input-wrapper">
                <input
                  pInputText
                  [id]="control.qname"
                  [formControlName]="control.qname"
                  [readonly]="control.ui.readonly || false"
                  class="w-full"
                />
              </div>
            </div>
          </ng-container>

          <!-- Select/Dropdown Fields (new fields) -->
          <!-- Exclude business table fields (they have their own dedicated table) -->
          <ng-container *ngFor="let control of schema.controls">
            <div
              class="form-field"
              [attr.data-qname]="control.qname"
              *ngIf="
                control.ui.control === 'select' &&
                !control.ui.businessTable &&
                control.ui.enumSource &&
                shouldShowInSection(control.qname, 'subsequent-changes')
              "
            >
              <label [for]="control.qname" class="field-label">
                {{ control.label }}<app-required-marker [when]="isFieldRequired(control)" />
              </label>

              <div class="field-input-wrapper">
                <p-select
                  [inputId]="control.qname"
                  [formControlName]="control.qname"
                  [options]="getDropdownOptions(control.ui.enumSource)"
                  optionLabel="label"
                  optionValue="value"
                  [placeholder]="control | fixedPlaceholder: 'Please select'"
                  class="w-full"
                  [filter]="true"
                  filterPlaceholder="Search..."
                [showClear]="true"
                ></p-select>

                <small
                  class="error-message"
                  *ngIf="
                    getControl(control.qname)?.invalid &&
                    getControl(control.qname)?.touched
                  "
                >
                  <span *ngIf="getControl(control.qname)?.errors?.['required']">
                    {{
                      control.error?.required ||
                        control.label + " is required." ||
                        "This is a mandatory field."
                    }}
                  </span>
                </small>

                <!-- Combined warning messages (non-blocking) -->
                <app-warning-message
                  [warnings]="getAllWarnings(control.qname)"
                ></app-warning-message>
              </div>
            </div>
          </ng-container>
        </div>
      </div>
    </form>

    <!-- Form Status -->
    <!-- <div *ngIf="!isLoading && schema && scopeForm" class="form-status mt-4">
      <p>
        <strong>Form Status:</strong>
        <span [class]="scopeForm.valid ? 'text-green-600' : 'text-red-600'">
          {{ scopeForm.valid ? 'Valid ✓' : 'Invalid - Please fill required fields' }}
        </span>
      </p>
      <p>
        <strong>Total Fields:</strong> {{ schema.controls.length || 0 }}
      </p>
      <p>
        <strong>Auto-save:</strong>
        <span class="text-green-600">Enabled (500ms debounce)</span>
      </p>
    </div> -->
    <submit-for-validation
      [form]="scopeForm"
      [sheetKey]="'scopeOfFiling'"
      [formTitle]="formTitle"
      [visibleFormData]="getVisibleFormData()"
      (cancelConfirmed)="onCancelConfirmed()"
    ></submit-for-validation>
  </p-card>
</div>
